import { RequireAtLeastOne } from '@thurstech/commons/dist/utils/types';
import { AxiosError } from 'axios';

import { Option } from '../components/input/InputSelect';
import { errorMessage, successMessage } from '../hooks/useNotifications';

export type FileType = RequireAtLeastOne<{
  obj: File;
  loc: string;
}>;

const getCloudfrontUrl = (loc: string) =>
  loc && loc.startsWith('prop') ? `/${encodeURIComponent(loc)}` : loc;
export const getPreviewUrl = (file?: FileType) =>
  file ? (file.obj ? URL.createObjectURL(file.obj) : getCloudfrontUrl(file.loc!)) : 'UndefinedObject';

/**
   * Method for getting unsigned decoded uri e.g. 
   * Input: https://test.s3.ap-south-1.amazonaws.com/video%2Fur%2FTest+testing%2Fm0h0tcvxt6zhj%2Btets?credentialToken=something
   * decodedS3Path: https://test.s3.ap-south-1.amazonaws.com/video/ur/Test+testing/m0h0tcvxt6zhj+tets
   * Output: video/ur/Test testing/m0h0tcvxt6zhj+tets

   * @param url The presigned Url for the resource
   * @returns unsigned decoded uri
   */
export const getS3Key = (s3Path: string): string => {
  if (!s3Path.startsWith('http')) {
    return s3Path;
  }

  return decodeURIComponent(new URL(s3Path).pathname.substring(1).replace(/\+/g, ' '));
};

const cities = [
  'Banglore',
  'Calcutta',
  'Chennai',
  'Delhi',
  'Hyderabad',
  'Jaipur',
  'Kochi',
  'Mumbai',
  'Panjim',
  'Pune',
  'Vishakapattanam'
];
export const cityOptions: Option[] = cities.map(city => ({ id: city, value: city }));

export const toError = (expectedError: unknown, unexpectedErrorMessage?: string): Error => {
  if (expectedError instanceof AxiosError && expectedError?.response?.data?.message) {
    return new Error(expectedError.response.data.message, { cause: expectedError });
  } else if (expectedError instanceof Error) {
    return expectedError;
  } else if (
    typeof expectedError === 'object' &&
    expectedError !== null &&
    'message' in expectedError &&
    typeof (expectedError as { message: unknown }).message === 'string'
  ) {
    return new Error((expectedError as { message: string }).message, { cause: expectedError });
  }

  // Fallback for other cases (e.g., primitive values)
  return new Error(unexpectedErrorMessage ?? 'Unexpected Error', { cause: expectedError });
};

export const withErrorHandler = async <T>(
  operation: () => Promise<T>,
  completeSuccessMessage?: string
): Promise<T> => {
  try {
    const res = await operation();
    if (completeSuccessMessage) {
      successMessage(completeSuccessMessage);
    }
    return res;
  } catch (err) {
    errorMessage(toError(err).message);
  }
  return null as T;
};
